// @flow
import { filter, keyBy, noop, omit, without } from 'lodash';

import {
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
  RESET_FRIEND_REQUESTS
} from '../constants/actionTypes';
import {
  ACCEPT_FRIEND_REQUEST,
  CREATE_FRIEND_REQUEST,
  FRIENDS,
  FRIEND_REQUESTS,
  REMOVE_FRIEND,
  REMOVE_FRIEND_REQUEST,
  SEARCH_USER,
  USER_SIGN_OUT,
  UPDATE_FRIEND_GROUPS
} from '../constants/apiTypes';
import { FRIEND } from '../constants/friendStatusTypes';
import decodeUser from '../normalizers/decodeUser';
import decodeFriendRequest from '../normalizers/decodeFriendRequest';
import createReducer from '../utils/createReducer';
import switchFunc from '../utils/switchFunc';

const initialState: FriendRequestsState = {
  hasInitialised: false,
  friendRequests: {},
  errors: null,
  isFetchingRequests: true,
  isCreatingFriendRequests: [],
  savingFriendRequests: new Set(),
  isDeletingFriendRequest: false,
  isUpdating: false
};

function onFetchFriends(
  state: FriendRequestsState,
  action: {
    friendRequests: Object
  }
) {
  return {
    hasInitialised: true,
    isFetchingRequests: false,
    friendRequests: keyBy(action.friendRequests.map(decodeFriendRequest), 'id')
  };
}

function onCreateFriendRequest(
  state: FriendRequestsState,
  action: { friendRequests: Object, groupId: number }
) {
  const requests = keyBy(action.friendRequests.map(decodeFriendRequest), 'id');
  return {
    isCreatingFriendRequests: [],
    friendRequests: {
      ...state.friendRequests,
      ...requests
    }
  };
}

function onRemoveFriendRequest(state: FriendRequestsState, action: { id: number }) {
  return {
    isDeletingFriendRequest: false,
    friendRequests: omit(state.friendRequests, action.id)
  };
}

function onAcceptFriendRequest(
  state: FriendRequestsState,
  action: {
    id: number,
    friend: Object
  }
) {
  return {
    savingFriendRequests: new Set(without(...state.savingFriendRequests, action.id)),
    friendRequests: omit(state.friendRequests, action.id)
  };
}

function onSearchUsers(state: FriendRequestsState, action: { users: Array<Object> }) {
  const users = action.users.map(decodeUser);
  const friends = new Set(users.filter(u => u.status === FRIEND).map(u => u.id));
  const newFriends = filter(state.friendRequests, f => friends.has(f.id));
  // search users returns the latest status results for users
  // use this to update our requests status
  if (newFriends.length > 0) {
    return {
      friendRequests: omit(state.friendRequests, req => friends.has(req.id))
    };
  }
  return {};
}

const apiRequestReducer = switchFunc(
  {
    [FRIEND_REQUESTS]: () => ({
      errors: null,
      isFetchingRequests: true
    }),
    [CREATE_FRIEND_REQUEST]: (state, action) => ({
      errors: null,
      isCreatingFriendRequests: action.requestIds
    }),
    [ACCEPT_FRIEND_REQUEST]: (state: FriendRequestsState, action) => ({
      errors: null,
      savingFriendRequests: new Set([...state.savingFriendRequests, action.friendId])
    }),
    [REMOVE_FRIEND]: () => ({
      errors: null,
      isDeletingFriend: true
    }),
    [REMOVE_FRIEND_REQUEST]: () => ({
      errors: null,
      isDeletingFriendRequest: true
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      errors: null,
      savingFriendRequests: new Set([...state.savingFriendRequests, action.friendId])
    })
  },
  () => noop
);

const apiSuccessReducer = switchFunc(
  {
    [FRIENDS]: onFetchFriends,
    [FRIEND_REQUESTS]: onFetchFriends,
    [REMOVE_FRIEND_REQUEST]: onRemoveFriendRequest,
    [ACCEPT_FRIEND_REQUEST]: onAcceptFriendRequest,
    [CREATE_FRIEND_REQUEST]: onCreateFriendRequest,
    [SEARCH_USER]: onSearchUsers,
    [USER_SIGN_OUT]: () => initialState,
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      savingFriendRequests: new Set(without(...state.savingFriendRequests, action.friendId))
    })
  },
  () => noop
);

const apiFailureReducer = switchFunc(
  {
    [FRIEND_REQUESTS]: (state, action) => ({
      errors: action.errors,
      isFetchinFriends: false
    }),
    [CREATE_FRIEND_REQUEST]: (state, action) => ({
      errors: action.errors,
      isCreatingFriendRequests: []
    }),
    [ACCEPT_FRIEND_REQUEST]: (state: FriendRequestsState, action) => ({
      errors: action.errors,
      savingFriendRequests: new Set(without(...state.savingFriendRequests, action.friendId))
    }),
    [REMOVE_FRIEND]: (state, action) => ({
      errors: action.errors,
      isDeletingFriend: false
    }),
    [REMOVE_FRIEND_REQUEST]: (state, action) => ({
      errors: action.errors,
      isDeletingFriendRequest: false
    }),
    [UPDATE_FRIEND_GROUPS]: (state: FriendState, action) => ({
      errors: action.errors,
      savingFriendRequests: new Set(without(...state.savingFriendRequests, action.friendId))
    })
  },
  () => noop
);

export default createReducer(initialState, {
  [API_CALL_REQUEST]: (state, action) => ({
    ...apiRequestReducer(action.api)(state, action)
  }),
  [API_CALL_SUCCESS]: (state, action) => ({
    ...apiSuccessReducer(action.api)(state, action)
  }),
  [API_CALL_FAILURE]: (state, action) => ({
    ...apiFailureReducer(action.api)(state, action)
  }),
  [RESET_FRIEND_REQUESTS]: () => initialState
});
